import { useField } from 'formik';
import isEqual from 'react-fast-compare';
import { useTranslation } from 'react-i18next';
import { Flex } from 'reflexbox';

import * as graphql from '@treams/treams-network';
import { Button } from '@treams/treams-ui';

import CompetencyInput from './input/CompetencyInput';
import OpenQuestionInput from './input/OpenQuestionInput';
import ReviewGoalsInput from './input/ReviewGoalsInput';
import SentimentInput from './input/SentimentInput';
import StarsInput from './input/StarsInput';

const supportedSectionItemTypes = {
	[graphql.EvaluationSectionItemType.OpenQuestion]: 'openQuestion',
	[graphql.EvaluationSectionItemType.Competency]: 'competency',
	[graphql.EvaluationSectionItemType.Sentiment]: 'sentiment',
	[graphql.EvaluationSectionItemType.Stars]: 'stars',
	[graphql.EvaluationSectionItemType.ReviewGoals]: 'reviewGoals',
};

function getFieldName(sectionIndex: number, itemIndex: number, fieldName: string) {
	return `sections[${sectionIndex}].items[${itemIndex}].${fieldName}`;
}

function ManualSave({
	isSaving,
	onSave,
	fieldName,
}: {
	isSaving: boolean;
	onSave: () => void;
	fieldName: string;
}) {
	const { t } = useTranslation();
	const [, { value, initialValue }, { setValue }] = useField(fieldName);

	const isDirty = !isEqual(value, initialValue);

	return (
		<Flex
			justifyContent="flex-start"
			flexWrap="wrap"
			alignItems="center"
			style={{ columnGap: '5px' }}
		>
			<Button
				mt={2}
				onClick={onSave}
				disabled={isSaving || !isDirty}
				loading={isSaving}
				title={t('conversations.module.answer.save')}
				small
			/>
			{isDirty && (
				<Button
					mt={2}
					title={t('conversations.module.answer.discard')}
					type="secondary"
					disabled={isSaving}
					onClick={() => setValue(initialValue)}
					small
				/>
			)}
		</Flex>
	);
}

export default function EvaluationAnswerComposer({
	goal,
	result,
	submission,
	autosave,
	section: sectionIndex,
	item: itemIndex,
	isSaving,
	onSave,
}: {
	autosave?: boolean;
	section: number;
	item: number;
	goal?: graphql.EvaluationSubmissionGoal;
	submission: graphql.EvaluationSubmission;
	result: graphql.EvaluationSubmissionResult;
	onSave?: () => void;
	isSaving: boolean;
}) {
	const item = submission.evaluationForm.sections[sectionIndex].items[itemIndex];

	const supportedSectionItem =
		supportedSectionItemTypes[
			submission.evaluationForm.sections[sectionIndex].items[itemIndex].type
		];

	const fieldName = getFieldName(sectionIndex, itemIndex, supportedSectionItem);

	if (!supportedSectionItem) {
		return null;
	}

	return (
		<>
			{item.type === graphql.EvaluationSectionItemType.OpenQuestion && (
				<OpenQuestionInput name={fieldName} />
			)}

			{item.type === graphql.EvaluationSectionItemType.Competency && (
				<CompetencyInput name={fieldName} competency={item.competency!} party={result.party} />
			)}

			{item.type === graphql.EvaluationSectionItemType.Sentiment && (
				<SentimentInput name={fieldName} />
			)}

			{item.type === graphql.EvaluationSectionItemType.Stars && <StarsInput name={fieldName} />}

			{item.type === graphql.EvaluationSectionItemType.ReviewGoals && (
				<ReviewGoalsInput
					name={fieldName}
					competencyLevelSet={item.reviewGoals!.competencyLevelSet}
					submission={submission}
					goal={goal!}
				/>
			)}

			{!autosave && <ManualSave isSaving={isSaving} onSave={onSave!} fieldName={fieldName} />}
		</>
	);
}
